import React, {
  createContext,
  FC,
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import type {
  ModalCloseEvent,
  ModalProps,
} from '@johnlewispartnership/wtr-ingredients/dist/ingredients/Modal/Modal';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';

import { modalSeverityValues } from 'constants/modals/modals';
import { DEFAULT_CUSTOMER_ID } from 'constants/variables';

type ValueOf<T> = T[keyof T];

export type CommonModalProps = ModalProps & {
  id: string;
  severity?: ValueOf<typeof modalSeverityValues>;
};

export type CommonModalContextValue = {
  state: CommonModalProps;
  openModal: (modalProps: CommonModalProps) => void;
  closeModal: (event?: ModalCloseEvent) => void;
};

export const CommonModalContext = createContext<CommonModalContextValue | undefined>(undefined);

export const CommonModalProvider: FC<{
  initialState?: CommonModalContextValue['state'];
  children: React.ReactNode;
}> = ({
  children,
  initialState = { isOpen: false, id: '', severity: modalSeverityValues.INFO },
}) => {
  const { trackEvent } = useAnalytics();
  const [modalProps, setModalProps] = useState<CommonModalProps>(initialState);

  const customerId = DEFAULT_CUSTOMER_ID;
  const wasOpenRef = useRef(modalProps.isOpen);

  useEffect(() => {
    const modalJustOpened = modalProps.isOpen && !wasOpenRef.current;

    if (modalJustOpened) {
      trackEvent({
        event: 'modal_opened',
        customerId,
        modal: {
          id: modalProps.id,
          title: modalProps.titleText,
          severity: modalProps.severity || modalSeverityValues.INFO,
        },
      });
      if (modalProps.severity === modalSeverityValues.ERROR) {
        const errorToSendToApm = new Error('Error modal opened');
        window?.apm?.captureError(
          Object.assign(errorToSendToApm, {
            event: 'modal_opened',
            modalId: modalProps.id,
            modalSeverity: modalProps.severity,
            modalTitle: modalProps.titleText,
          }),
        );
      }
    }

    // Update the ref for the next render
    wasOpenRef.current = modalProps.isOpen;
  }, [
    customerId,
    modalProps.isOpen,
    modalProps.id,
    modalProps.severity,
    modalProps.titleText,
    trackEvent,
  ]);

  const openModal = useCallback(
    (newModalProps: Omit<CommonModalProps, 'isOpen'>) =>
      setModalProps({ ...newModalProps, isOpen: true } as CommonModalProps),
    [],
  );

  const closeModal = useCallback(
    (event?: ModalCloseEvent) => {
      trackEvent({
        event: 'modal_closed',
        customerId,
        modal: {
          id: modalProps.id,
          title: modalProps.titleText,
          severity: modalProps.severity || modalSeverityValues.INFO,
          ...(event?.label && { reason: event.label }),
        },
      });
      setModalProps({ ...modalProps, isOpen: false });
    },
    [customerId, modalProps, trackEvent],
  );

  const value: CommonModalContextValue = useMemo(
    () => ({
      state: modalProps,
      openModal,
      closeModal,
    }),
    [modalProps, openModal, closeModal],
  );
  return <CommonModalContext.Provider value={value}>{children}</CommonModalContext.Provider>;
};

export const useCommonModal = (): CommonModalContextValue => {
  const context = useContext(CommonModalContext);

  if (context === undefined) {
    throw new Error('useCommonModal must be used within a CommonModalProvider');
  }

  return context;
};

export const withCommonModal = <ComponentProps extends CommonModalContextValue>(
  Component: React.ComponentType<ComponentProps>,
) => {
  const ComponentWithCommonModal = (props: Omit<ComponentProps, keyof CommonModalContextValue>) => {
    const contextValue = useCommonModal();
    const componentProps = { ...props, ...contextValue } as ComponentProps;

    return <Component {...componentProps} />;
  };
  return ComponentWithCommonModal;
};
